import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, MenuItem, TextField } from '@mui/material'
import React from 'react'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { CheckIcon, PlusIcon } from '../../../template/kartera/icons'
import { RAMO_OPTIONS, useGoalListModel } from './goal-list-model'

const selectMenu = { MenuProps: { slotProps: { paper: { className: 'v2-select-menu' } } } }

const primaryBtnClass = 'inline-flex h-11 items-center gap-2 rounded-xl border-0 bg-k-primary px-[18px] text-sm font-bold !text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40'
const outlineBtnClass = 'inline-flex h-11 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover disabled:cursor-not-allowed disabled:opacity-40'
const dangerBtnClass = 'inline-flex h-11 items-center gap-2 rounded-xl border-0 bg-k-danger px-4 text-sm font-bold !text-white hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40'

const GoalList = () => {
    const model = useGoalListModel()

    return (
        <KarteraAdminTemplate loading={false} title="Metas">
            <Dialog open={model.deleteOpen} onClose={model.cancelDelete} slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Eliminar meta</DialogTitle>
                <DialogContent>
                    <p className="m-0 text-sm text-k-ink-2">¿Desea eliminar la meta? Esta acción no se puede deshacer.</p>
                </DialogContent>
                <DialogActions className="gap-1 px-6 pb-5">
                    <button type="button" className={outlineBtnClass} onClick={model.cancelDelete}>Cancelar</button>
                    <button type="button" className={dangerBtnClass} onClick={model.confirmDelete} disabled={model.deleting}>
                        {model.deleting ? 'Eliminando…' : 'Eliminar'}
                    </button>
                </DialogActions>
            </Dialog>

            <div className="relative mx-auto flex w-full max-w-[880px] flex-col gap-[18px]">
                {model.loading && (
                    <div className="absolute inset-0 z-40 flex items-start justify-center rounded-2xl bg-k-bg/80 pt-24" role="status">
                        <div className="flex flex-col items-center gap-3 rounded-2xl border border-k-line bg-k-surface px-8 py-6 shadow-k-pop">
                            <RefreshIcon className="animate-spin text-brand-indigo" />
                            <p className="m-0 text-sm font-semibold text-k-ink">Cargando metas…</p>
                        </div>
                    </div>
                )}

                <KarteraPageHeader
                    title="Metas"
                    description="Define cuántas pólizas y cuánta prima neta quieres colocar por año o por mes."
                    backTo={model.backTo}
                    backLabel="Configuración"
                    actions={(
                        <button type="button" className={primaryBtnClass} onClick={model.addGoal} disabled={model.loading}>
                            <PlusIcon />
                            Agregar meta
                        </button>
                    )}
                />

                {model.error && (
                    <div className="flex items-center gap-3 rounded-[14px] bg-k-danger-soft px-4 py-3.5 text-sm text-k-danger-ink">
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.error}</p>
                    </div>
                )}

                {!model.loading && !model.error && model.goals.length === 0 && (
                    <section className="k-card items-start">
                        <h2 className="k-card-title">Sin metas</h2>
                        <p className="m-0 text-sm text-k-ink-2">Aún no configuras metas. Agrega una para dar seguimiento a tus ventas.</p>
                        <button type="button" className={primaryBtnClass} onClick={model.addGoal}>
                            <PlusIcon />
                            Agregar meta
                        </button>
                    </section>
                )}

                {model.goals.map((goal) => (
                    <section key={goal.key} className="overflow-hidden rounded-2xl border border-k-line bg-k-surface">
                        <button
                            type="button"
                            aria-expanded={goal.open}
                            onClick={() => model.toggle(goal.key)}
                            className="flex w-full cursor-pointer items-center gap-3.5 border-0 bg-transparent px-5 py-4 text-left hover:bg-k-hover"
                        >
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-tone-orange text-tone-orange-ink">
                                <FlagOutlinedIcon style={{ fontSize: 22 }} />
                            </span>
                            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                                <span className="flex flex-wrap items-center gap-2">
                                    <span className="truncate text-sm font-bold text-k-ink">{goal.title}</span>
                                    <span className="rounded-full bg-k-accent-soft px-2 py-0.5 text-xs font-bold text-k-accent-ink">{goal.periodLabel}</span>
                                    {goal.isNew && <span className="rounded-full bg-k-line-soft px-2 py-0.5 text-[11px] font-bold text-k-muted">Sin guardar</span>}
                                </span>
                                <span className="text-[13px] tabular-nums text-k-muted">{goal.summary}</span>
                            </span>
                            <ExpandMoreIcon className={`shrink-0 text-k-muted transition-transform ${goal.open ? 'rotate-180' : ''}`} />
                        </button>

                        {goal.open && (
                            <form onSubmit={(event) => model.save(event, goal.key)} className="flex flex-col gap-5 border-t border-k-line-soft px-5 pb-5 pt-4">
                                <h3 className="k-card-title">Configuración</h3>
                                <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
                                    <TextField
                                        className="v2-field v2-select" variant="filled" fullWidth select required
                                        label="Aseguradora"
                                        value={goal.aseguradora}
                                        onChange={(event) => model.change.aseguradora(goal.key, event.target.value)}
                                        slotProps={{ select: selectMenu }}
                                    >
                                        {model.insurers.map((insurer) => <MenuItem key={insurer.id} value={insurer.id}>{insurer.label}</MenuItem>)}
                                    </TextField>
                                    <TextField
                                        className="v2-field v2-select" variant="filled" fullWidth select required
                                        label="Ramo del negocio"
                                        value={goal.ramo}
                                        onChange={(event) => model.change.ramo(goal.key, event.target.value)}
                                        slotProps={{ select: selectMenu }}
                                    >
                                        {RAMO_OPTIONS.map((ramo) => <MenuItem key={ramo.id} value={ramo.id}>{ramo.label}</MenuItem>)}
                                    </TextField>
                                    <TextField
                                        className="v2-field v2-select" variant="filled" fullWidth select required
                                        label="Año"
                                        value={goal.anio}
                                        onChange={(event) => model.change.anio(goal.key, Number(event.target.value))}
                                        slotProps={{ select: selectMenu }}
                                    >
                                        {model.yearOptions.map((year) => <MenuItem key={year} value={year}>{year}</MenuItem>)}
                                    </TextField>
                                    <TextField
                                        className="v2-field v2-select" variant="filled" fullWidth select required
                                        label="Periodo"
                                        helperText="Meta anual o de un mes específico."
                                        value={goal.periodo}
                                        onChange={(event) => model.change.periodo(goal.key, event.target.value)}
                                        slotProps={{ select: selectMenu }}
                                    >
                                        {model.periodOptions.map((option) => <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>)}
                                    </TextField>
                                </div>

                                <h3 className="k-card-title">Valores de la meta</h3>
                                <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-2">
                                    <TextField
                                        className="v2-field" variant="filled" fullWidth required
                                        label="Número de pólizas"
                                        value={goal.polizas}
                                        onChange={(event) => model.change.polizas(goal.key, event.target.value)}
                                        onFocus={(event) => event.target.select()}
                                        slotProps={{ htmlInput: { inputMode: 'numeric', style: { textAlign: 'right' } } }}
                                    />
                                    <TextField
                                        className="v2-field" variant="filled" fullWidth required
                                        label="Prima neta"
                                        value={goal.primas}
                                        onChange={(event) => model.change.primas(goal.key, event.target.value)}
                                        onBlur={(event) => model.change.primasBlur(goal.key, event.target.value)}
                                        onFocus={(event) => event.target.select()}
                                        slotProps={{
                                            htmlInput: { inputMode: 'decimal', style: { textAlign: 'right' } },
                                            input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
                                        }}
                                    />
                                </div>

                                {goal.message && (
                                    <div className={`flex items-center gap-3 rounded-[14px] px-4 py-3.5 text-sm ${goal.message.type === 'error' ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-k-ok-soft text-k-ok'}`}>
                                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                                        <p className="m-0">{goal.message.message}</p>
                                    </div>
                                )}

                                <div className="flex flex-wrap items-center justify-between gap-2.5">
                                    <button type="button" className={outlineBtnClass} onClick={() => model.askDelete(goal.key)} disabled={goal.saving}>
                                        <DeleteOutlineIcon style={{ fontSize: 18 }} className="text-k-danger" />
                                        {goal.isNew ? 'Descartar' : 'Eliminar meta'}
                                    </button>
                                    <button type="submit" className={primaryBtnClass} disabled={goal.saving}>
                                        {goal.saving ? <RefreshIcon className="animate-spin" style={{ fontSize: 18 }} /> : <CheckIcon />}
                                        {goal.saving ? 'Guardando…' : 'Guardar meta'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </section>
                ))}
            </div>
        </KarteraAdminTemplate>
    )
}

export default GoalList
